"use client";

import { ArrowRight, Anchor, Waves, Fish } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const advantages = [{
  number: "01",
  title: "Wild-caught, never farmed.",
  description: "We source exclusively from the open ocean, ensuring every catch is wild and natural. Our commitment to the sea means we only harvest what nature provides, preserving the integrity of the Gulf.",
  Icon: Fish
}, {
  number: "02",
  title: "Sustainable harvesting practices.",
  description: "We partner with local fishermen who utilize responsible, low-impact methods. By respecting migration patterns and seasonal limits, we ensure the bounty of the sea remains for generations.",
  Icon: Waves
}, {
  number: "03",
  title: "Heritage-backed quality.",
  description: "Built by hand and proven by time, our vessel, The Miss Yvonne, represents decades of maritime expertise. We handle every fish with the care and precision that only a family-owned legacy can provide.",
  Icon: Anchor
}];
function AdvantageBlock({
  number,
  title,
  description,
  Icon,
  index
}: {
  number: string;
  title: string;
  description: string;
  Icon: typeof Fish;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 100}ms`
  }} className={`group relative border-t border-[hsl(38_20%_85%)] py-12 md:py-16 transition-all duration-700 ease-out hover:bg-[hsl(215_60%_35%/0.05)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${index === advantages.length - 1 ? "border-b" : ""}`}>
      <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-px bg-[hsl(215_60%_35%)] scale-y-0 group-hover:scale-y-100 origin-top transition-transform duration-500 ease-out" />
      <div className="flex items-start justify-between gap-6 mb-6">
        <span className="font-mono text-sm tracking-wider text-[hsl(215_60%_35%)] tabular-nums" style={{
        fontVariantNumeric: "tabular-nums"
      }}>
          {number}
        </span>
        <Icon className="w-7 h-7 text-[hsl(222_47%_11%)] stroke-[1.25] opacity-80" aria-hidden="true" />
      </div>
      <h3 className="font-default text-2xl md:text-[28px] leading-tight font-medium text-[hsl(222_47%_11%)] mb-4 tracking-tight">
        {title}
      </h3>
      <p className="font-default text-base md:text-lg leading-relaxed text-[hsl(210_10%_45%)] max-w-xl">
        {description}
      </p>
    </div>;
}
export default function Sustainability() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="2443" id="from-shore-to-door" className="bg-[hsl(40_33%_96%)] text-[hsl(222_47%_11%)] py-24 md:py-40 px-6 md:px-12 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={introRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-[hsl(215_60%_35%)] mb-8">
                Our Commitment
              </p>
              <h2 className="font-default text-5xl md:text-6xl lg:text-[72px] leading-[1.05] tracking-tight text-[hsl(222_47%_11%)] mb-8">
                Wild-Caught. Delivered with Care.
              </h2>
              <p className="font-default text-lg leading-relaxed text-[hsl(210_10%_45%)] mb-10 max-w-md">
                The Miss Yvonne is more than a vessel; it is a promise of quality. 
                We bridge the gap between the Gulf Coast and your kitchen, 
                delivering premium, wild-caught seafood with the respect it deserves.
              </p>
              <Link to="/SustainabilityPage" className="group inline-flex items-center gap-2 font-default text-base font-medium text-[hsl(222_47%_11%)] border-b border-[hsl(222_47%_11%)] pb-1 hover:text-[hsl(215_60%_35%)] hover:border-[hsl(215_60%_35%)] transition-colors duration-300">
                Read our story
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            {advantages.map((advantage, i) => <AdvantageBlock key={i} index={i} number={advantage.number} title={advantage.title} description={advantage.description} Icon={advantage.Icon} />)}
          </div>
        </div>
      </div>
    </section>;
}